import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FamilyFolderModel } from '../../../models/family-folder.model';
import { ProductWizard } from '../../../state/product-wizard/product-wizard.model';
import {
    BreadcrumbItem,
    BreadcrumbNavigateEventDetail,
    FamilySelectorClickEventDetail,
    TabOption,
    TabsChangeEventDetail,
} from '@flowww/apps-design-system';
import {
    buildBreadcrumbTrail,
    computeDefaultFamilyPath,
    countItemsInFamily,
    mapFamilyTab,
    resolveFamilyByPath,
} from '../../../../core/utils/family-tabs.util';

const OTHERS_TAB_VALUE = '0';

@Component({
    standalone: false,
    selector: 'app-family-product-list',
    templateUrl: './family-product-list.component.html',
    styleUrls: ['./family-product-list.component.scss'],
})
export class FamilyProductListComponent implements OnInit, OnChanges {
    @Input() families: FamilyFolderModel[];
    @Input() isLoadingContent: boolean;
    @Input() productsWizard: ProductWizard[];
    @Input() appDelegate: boolean;
    @Input() hideGiftBanner: boolean = false;
    @Input() searchText: string = '';

    @Output() productToggleEvent = new EventEmitter<any>();
    @Output() productClickToPresentModalEvent = new EventEmitter<any>();
    /** Flowwwer template */
    @Output() productClickEvent = new EventEmitter<any>();

    categoryTabs: TabOption[] = [];
    activeCategory: string;
    activeCategoryPath: string[] = [];

    constructor(private translateService: TranslateService) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.families || changes.productsWizard) {
            this.buildCategoryTabs();
        }
    }

    buildCategoryTabs(): void {
        const tabs: TabOption[] = (this.families ?? []).map((family) => mapFamilyTab(family));

        if (this.productsWizard?.length > 0) {
            tabs.push({
                label: this.translateService.instant('lbl_others'),
                value: OTHERS_TAB_VALUE,
            });
        }

        this.categoryTabs = tabs;

        if (!tabs.some((tab) => tab.value === this.activeCategory)) {
            this.activeCategory = tabs[0]?.value;
            this.activeCategoryPath = this.activeCategory
                ? computeDefaultFamilyPath(this.activeCategory)
                : [];
        }
    }

    get showFamilyTabs(): boolean {
        return this.families?.length > 0;
    }

    /** The family folder the user has drilled down into. */
    get currentFamilyFolder(): FamilyFolderModel | undefined {
        return resolveFamilyByPath(this.families, this.activeCategoryPath);
    }

    /** Subfamilies of the current folder, rendered as `flw-family-selector` */
    get currentSubfamilies(): FamilyFolderModel[] {
        if (this.activeCategory === OTHERS_TAB_VALUE) {
            return [];
        }
        return this.currentFamilyFolder?.families ?? [];
    }

    /** Path above the root tab, to feed `flw-breadcrumb`. */
    get breadcrumbItems(): BreadcrumbItem[] {
        return buildBreadcrumbTrail(this.families, this.activeCategoryPath);
    }

    get visibleProducts(): ProductWizard[] {
        if (this.activeCategory === OTHERS_TAB_VALUE) {
            return this.productsWizard ?? [];
        }
        return this.currentFamilyFolder?.products ?? [];
    }

    get hasGiftableProducts(): boolean {
        return this.visibleProducts.some((product) => product.giftableVoucher);
    }

    get hasFlatGiftableProducts(): boolean {
        return (this.productsWizard ?? []).some((product) => product.giftableVoucher);
    }

    onCategoryChange(event: CustomEvent<TabsChangeEventDetail>) {
        this.activeCategory = event.detail.value;
        this.activeCategoryPath = computeDefaultFamilyPath(event.detail.value);
    }

    onFamilyDrillDown(event: CustomEvent<FamilySelectorClickEventDetail>) {
        this.activeCategoryPath = [...this.activeCategoryPath, event.detail.value];
    }

    onBreadcrumbNavigate(event: CustomEvent<BreadcrumbNavigateEventDetail>) {
        this.activeCategoryPath = this.activeCategoryPath.slice(0, event.detail.index + 1);
    }

    countItems(family: FamilyFolderModel): number {
        return countItemsInFamily(family);
    }

    onProductToggle(productWizard) {
        this.productToggleEvent.emit(productWizard);
    }

    onProductClickToPresentModal(productWizard) {
        this.productClickToPresentModalEvent.emit(productWizard);
    }

    /** Flowwwer template */
    onProductClick(product) {
        this.productClickEvent.emit(product);
    }

    trackByFamilyId(_index: number, family: FamilyFolderModel): string {
        return family.id;
    }
}
